import React, { Fragment } from 'react';
import size  from 'lodash/size';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import every from 'lodash/every';
import { COURSE_TABS } from '../../../actions/constants';
import style from './QuantQualInsights.module.css';
import {
  WIDGET_TYPE_SRL_ACADEMICS_CHART, WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART,
  WIDGET_TYPE_SRL_CHALLENGES_CHART,
  WIDGET_TYPE_SRL_CONFIDENCE_CHART, WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET,
  WIDGET_TYPE_SRL_GOAL_CHART, WIDGET_TYPE_SRL_GROWTH_CHART, WIDGET_TYPE_SRL_HELP_AND_COLLABORATION,
  WIDGET_TYPE_SRL_HOURS_CHART, WIDGET_TYPE_SRL_SPACING_PACING_CHART,
  SRL_COMPONENT_TYPES,
} from "../../constants";
import {
  selectAverageData,
  selectBarChartData,
  selectChartData,
  selectPercentageData,
  selectStackedBardChartData,
  selectTextListData
} from "../../utils";
import { selectIntroAveragesData } from "./components/SRLAcademicContent/helper";

const NO_DATA_MESSAGE = 'There isn’t any data to display at this time. You may need to change your date range to see student activity.';

// @TODO: Text content should added to config variables instead of hard-coded
export const nullStateMessages = {
  externalFrameworkHasNoResponses: () => (
    <Fragment>
      <h2>External Framework Has No Responses</h2>
      <h4>Content will be visible after responses provided</h4>
    </Fragment>
  ),
  externalFrameworkHasNoData: () => (
    <Fragment>
      {NO_DATA_MESSAGE}
    </Fragment>
  ),
  notStarted: () => (
    <Fragment>
      No students have started assignments in this unit. As they work on assignments with Learning Objectives, you will see their performance updated here.
    </Fragment>
  ),
  noStudents: (onTabChange) => {
    const tab = COURSE_TABS.PEOPLE.id;
    return (
      <Fragment>
        No students have enrolled in this course.<br /> Go to the <button
          className={style.link}
          role="link"
          onClick={(e) => {
            e.stopPropagation();
            onTabChange(tab);
          }}
        >People</button> tab to invite students to enroll.
      </Fragment>
    );
  },
  noAssignmentsOrLearningObjectives: (onTabChange) => {
    const tab = COURSE_TABS.ASSIGNMENTS.id;
    return (
      <Fragment>
        There was no student activity on recent or upcoming assignments this week.<br /> Go to the <button
          className={style.link}
          role="link"
          onClick={(e) => {
            e.stopPropagation();
            onTabChange(tab);
          }}
        >Course Planner</button> tab to set up and manage assignments.
      </Fragment>
    );
  },
  noAssignmentsWithStudentActivity: (onTabChange) => {
    const tab = COURSE_TABS.ASSIGNMENTS.id;
    return (
      <Fragment>
        There are no assignments with student activity yet.<br /> Go to the <button
          role="link"
          className={style.link}
          onClick={(e) => {
            e.stopPropagation();
            onTabChange(tab);
          }}
        >Course Planner</button> tab to setup and manage assignments.
      </Fragment>
    );
  },
  noDataMessage: () => <Fragment>
    {NO_DATA_MESSAGE}
  </Fragment>,
  noDataForAssignments: () => <Fragment>
    {NO_DATA_MESSAGE}
  </Fragment>,
  noScoresForAssignment: () => <Fragment>
    There aren’t any scores available for this assignment yet.
  </Fragment>,
  noSelectedAssignment: () => <Fragment>
    The selected activity doesn’t have any data yet.
  </Fragment>,
  noDataForLogin: () => <Fragment>
    There isn’t any student login data to display at this time. You may need to change your date range to see student activity.
  </Fragment>,
};

export const getNullStateMessage = (onTabChange, activeUnit, notStarted, hasStudents, hasLearningObjectives) => {
  if (!hasStudents) {
    return nullStateMessages.noStudents(onTabChange);
  } else if (!activeUnit || !hasLearningObjectives || notStarted) {
    return nullStateMessages.noAssignmentsOrLearningObjectives(onTabChange);
  }
  return null;
};

const isAnEmptyColumnChart = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const columnChart = selectChartData(insightsData) || [];
  return size(srlData) === 0 || columnChart.length === 0 || every(columnChart, (item = {}) =>  isNil(item.student_count) || +item.student_count === 0);
};

const isAnEmptyTextList = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const textList = get(selectTextListData(insightsData), 'studentResponses');
  return size(srlData) === 0 || textList.length === 0;
};

const isAnEmptyAverage = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const averageData = selectAverageData(insightsData) || {};
  return size(srlData) === 0 || isNil(averageData.average);
};

const isAnEmptyPercentage = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const percentageData = selectPercentageData(insightsData) || {};
  return size(srlData) === 0 || isNil(percentageData.percent);
};

const isAnEmptyBarChartData = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const barChartData = selectBarChartData(insightsData) || {};
  return size(srlData) === 0 || barChartData.length === 0 || every(barChartData, (item) => isNil(item.student_count) || +item.student_count === 0);
};

const isAnEmptyStackedBardChart = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const stackedBarData = selectStackedBardChartData(insightsData) || [];
  const noStudentCount = +get(stackedBarData, '[0].student_count', 0);
  const yesStudentCount = +get(stackedBarData, '[1].student_count', 0);
  return size(srlData) === 0 || stackedBarData.length === 0 || (!noStudentCount && !yesStudentCount);
};

const isAnEmptyIntroAverage = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const introAverageData = selectIntroAveragesData(insightsData) || [];
  return size(srlData) === 0 || introAverageData.length === 0 || every(introAverageData, (item) => isNil(item.percent) || isNil(item.label));
};

const isAnEmptyFrequencyStackedBar = (srlData) => {
  const insightsData = get(srlData, 'insightsData');
  const stackedBarData = selectStackedBardChartData(insightsData) || [];
  return size(srlData) === 0 || stackedBarData.length === 0 || every(stackedBarData, (item) => isNil(item.student_count) || +item.student_count === 0);
};

export const isAnEmptySRLWidget = (srlData, widgetType, isSRLIntroSurvey) => {
  switch (widgetType) {
    case WIDGET_TYPE_SRL_CONFIDENCE_CHART: {
      return (isSRLIntroSurvey) ?
        isAnEmptyColumnChart(srlData) && isAnEmptyTextList(srlData) && isAnEmptyAverage(srlData) :
        isAnEmptyColumnChart(srlData) && isAnEmptyAverage(srlData);
    }
    case WIDGET_TYPE_SRL_GOAL_CHART: {
      const chartColumns = get(srlData, 'insightsData',  []).filter(component => component.componentType === SRL_COMPONENT_TYPES.COLUMN_CHART);
      const dataToCheck = chartColumns.length ?
        chartColumns.map(chartColumn => ({ insightsData: [chartColumn] })) : // this is required because we didn't expect to receive 2 components type equal in the same card
        [srlData];

      return (isSRLIntroSurvey) ?
        every(dataToCheck, isAnEmptyColumnChart) :
        isAnEmptyStackedBardChart(srlData) && isAnEmptyTextList(srlData);
    }
    case WIDGET_TYPE_SRL_HOURS_CHART: {
      return isAnEmptyColumnChart(srlData);
    }
    case WIDGET_TYPE_SRL_CHALLENGES_CHART: {
      return (isSRLIntroSurvey) ?
        isAnEmptyTextList(srlData) :
        isAnEmptyAverage(srlData) && isAnEmptyBarChartData(srlData);
    }
    case WIDGET_TYPE_SRL_GROWTH_CHART: {
      return (isSRLIntroSurvey) ?
        isAnEmptyBarChartData(srlData) && isAnEmptyTextList(srlData) :
        isAnEmptyTextList(srlData);
    }
    case WIDGET_TYPE_SRL_ACADEMICS_CHART: {
      const academicColumns = get(srlData, 'insightsData',  []).filter(component => component.componentType === SRL_COMPONENT_TYPES.COLUMN_CHART);
      const academicChartData = academicColumns.length ?
        academicColumns.map(academicColumn => ({ insightsData: [academicColumn] })) : // Checks for 2 components of the same type in the same card
        [srlData];
      return (isSRLIntroSurvey) ?
        isAnEmptyIntroAverage(srlData) && isAnEmptyTextList(srlData) :
        every(academicChartData, isAnEmptyColumnChart) && isAnEmptyPercentage(srlData);
    }
    case WIDGET_TYPE_SRL_SPACING_PACING_CHART:
    case WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART:
    case WIDGET_TYPE_SRL_HELP_AND_COLLABORATION:
    case WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET: {
      return (isSRLIntroSurvey) ?
        isAnEmptyBarChartData(srlData) :
        isAnEmptyFrequencyStackedBar(srlData);
    }

    default: return false;
  }
};
